<template>
    <div
        ref="anchor"
        :class="['dropdown multiselectable-dropdown', { focused: isListOpen }]"
    >
        <input
            v-if="typable"
            :id="id"
            ref="input"
            v-model="text"
            type="text"
            :class="['input-no-border', { 'out-of-focus': !isListOpen }]"
            @click="onInputClick"
            @keydown.enter.prevent="onInputEnter"
            @keydown.down.prevent="onArrowDown"
            @keydown.up.prevent="onArrowUp"
            @keydown.space="onSpace"
            @keydown.esc="onEsc"
        />
        <div v-else :id="id" class="input-no-border" @click="onInputClick">
            <span v-if="checkedNames">{{ checkedNames }}</span>
            <span v-else class="dropdown-placeholder">{{ placeholder }}</span>
        </div>
        <teleport to="body">
            <div
                v-if="isListOpen"
                ref="list"
                v-focus-out="onCloseList"
                :class="[
                    'list-wrapper list-wrapper--multiselectable',
                    { 'list-wrapper--flipped': isListFlipped },
                ]"
                :style="listStyle"
                @click="onListWrapperClick()"
            >
                <span class="close" @click="onCloseList">&times;</span>
                <ul>
                    <li
                        v-for="(option, index) in filteredOptions"
                        :key="`option-${index}`"
                        :class="{
                            highlighted: highlightedName === option.name,
                        }"
                        @mouseover="highlightedName = option.name"
                    >
                        <label :for="`option-${index}`">
                            <input
                                :id="`option-${index}`"
                                v-model="checkedOptions"
                                type="checkbox"
                                :value="option"
                            />
                            <span class="small-checkbox"></span>
                            {{ option.name }}
                        </label>
                    </li>
                    <template v-if="typable && insertable">
                        <li
                            v-for="(option, index) in filteredNewOptions"
                            :key="`new-option-${index}`"
                            :class="{
                                highlighted: highlightedName === option.name,
                            }"
                            @mouseover="highlightedName = option.name"
                        >
                            <label :for="`new-option-${index}`">
                                <input
                                    :id="`new-option-${index}`"
                                    v-model="checkedOptions"
                                    type="checkbox"
                                    :value="option"
                                />
                                <span class="small-checkbox"></span>
                                {{ option.name }}
                            </label>
                        </li>
                        <li
                            v-if="text.trim().length && isOptionUnique"
                            :class="[
                                'new-entry',
                                { highlighted: highlightedName === text },
                            ]"
                            @mouseover="highlightedName = text"
                        >
                            <label for="option-to-be-added">
                                <input
                                    id="option-to-be-added"
                                    type="checkbox"
                                    @click="onAddOption($event)"
                                />
                                <span class="small-checkbox"></span>
                                {{ uppercaseText }}
                                <span class="new-entry-text">(new)</span>
                            </label>
                        </li>
                        <li
                            v-if="!text.length && newPromptText"
                            class="add-new-prompt"
                        >
                            <span>
                                {{ newPromptText }}
                            </span>
                        </li>
                    </template>
                    <li v-else-if="!filteredOptions.length" class="no-matches">
                        No matches found.
                    </li>
                </ul>
            </div>
        </teleport>
    </div>
    <div v-if="typable && !isListOpen" class="dropdown-checked-options">
        <span>{{ checkedNames }}</span>
    </div>
</template>
<script>
import BaseAdvancedDropdown from "js/components/base/BaseAdvancedDropdown.js";

export default {
    name: "MultiselectableDropdown",
    extends: BaseAdvancedDropdown,
    props: {
        modelValue: {
            type: Array,
            default: () => {
                return [];
            },
        },
        loaded: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["update:modelValue"],
    data() {
        return {
            isListOpen: false,
            highlightedName: "",
            text: "",
            newOptions: [],
            checkedOptions: [],
        };
    },
    computed: {
        checkedNames() {
            const names = this.checkedOptions.map((el) => el.name);

            return names.join(", ");
        },
        keyboardOptions() {
            const names = this.filteredOptions.map((o) => o.name);
            if (this.typable && this.insertable) {
                names.push(...this.filteredNewOptions.map((o) => o.name));
                if (this.text.trim().length && this.isOptionUnique) {
                    names.push(this.text);
                }
            }

            return names;
        },
    },
    watch: {
        loaded: {
            immediate: true,
            handler() {
                // form data has been retrieved
                this.checkedOptions = this.modelValue;
            },
        },
        modelValue(value) {
            if (value !== this.checkedOptions) {
                this.checkedOptions = value;
            }
        },
        checkedOptions(value) {
            // keep the parent in sync while the list is still open
            this.$emit("update:modelValue", value);
        },
        text() {
            this.highlightedName = "";
        },
    },
    methods: {
        onCloseList() {
            this.isListOpen = false;
            this.highlightedName = "";
            this.text = "";
            this.$emit("update:modelValue", this.checkedOptions);
        },
        onEsc() {
            this.onCloseList();
        },
        onAddOption(event) {
            event.stopPropagation();
            this.onListWrapperClick();
            this.confirmNewEntry();
        },
        onRemoveOption(option) {
            const optionIndex = this.checkedOptions.findIndex((el) => {
                return el.name === option.name;
            });

            this.checkedOptions.splice(optionIndex, 1);
            this.$emit("update:modelValue", this.checkedOptions);
        },
        onInputClick() {
            this.isListOpen = true;
        },
        onInputEnter(event) {
            event.preventDefault();

            if (this.highlightedName) {
                this.toggleHighlighted();

                return;
            }

            if (this.typable && this.insertable) {
                this.confirmNewEntry();
            }
        },
        onArrowDown() {
            if (!this.isListOpen) {
                this.isListOpen = true;
            }

            this.highlightedName = this.stepHighlighted(
                this.keyboardOptions,
                this.highlightedName,
                1
            );
        },
        onArrowUp() {
            if (!this.isListOpen) {
                this.isListOpen = true;
            }

            this.highlightedName = this.stepHighlighted(
                this.keyboardOptions,
                this.highlightedName,
                -1
            );
        },
        onSpace(event) {
            if (!this.highlightedName) {
                return;
            }

            event.preventDefault();
            this.toggleHighlighted();
        },
        toggleHighlighted() {
            const existing =
                this.filteredOptions.find(
                    (o) => o.name === this.highlightedName
                ) ??
                this.filteredNewOptions.find(
                    (o) => o.name === this.highlightedName
                );

            if (existing) {
                this.toggleOption(existing);

                return;
            }

            if (
                this.highlightedName === this.text &&
                this.text.trim().length &&
                this.isOptionUnique
            ) {
                this.confirmNewEntry();
            }
        },
        toggleOption(option) {
            const optionIndex = this.checkedOptions.findIndex(
                (el) => el.name === option.name
            );

            if (optionIndex === -1) {
                this.checkedOptions.push(option);
            } else {
                this.checkedOptions.splice(optionIndex, 1);
            }
        },
        confirmNewEntry() {
            this.text = this.text.trim();

            if (!this.text.length || !this.isOptionUnique) {
                return;
            }

            const newOption = { name: this.uppercaseText };
            this.text = "";
            this.newOptions.push(newOption);
            this.checkedOptions.push(newOption);
            this.highlightedName = "";
        },
    },
};
</script>
